import { create } from 'zustand'

// Mirrors apps/mobile/src/store/authStore.ts shape. The web app rehydrates
// from localStorage on startup (see App.tsx) so the store is the source
// of truth in-memory while session tokens / user JSON live in localStorage.

export interface User {
  id: number
  firstName: string
  lastName: string
  email: string
  profilePhoto: string | null
  // App UI locale ('en', 'te', …). Drives i18n string lookup.
  // NOT used for kinship term rendering.
  preferredLanguage: string
  gender?: 'male' | 'female' | null
  // How Telugu kinship terms are displayed.
  // Independent of preferredLanguage.
  kinshipLabelPreference?: 'telugu_script' | 'romanized' | 'both'
  // Admin role. Sourced from the backend authResponse; gates /admin/*
  // routes client-side via AdminRoute (server enforces with 403). Newly
  // promoted users need to log out + back in for this to flip true — the
  // server-side flag changes via SQL UPDATE but the client only refreshes
  // its User blob on fresh login. v1 bootstrap UX.
  isAdmin?: boolean
  // Self-delete grace marker (#4). Populated when the user has scheduled
  // their account for 30-day hard-erase. Drives the persistent erase-
  // pending banner. null when no erase is pending.
  eraseRequestedAt?: string | null
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  setAuth: (user: User, token: string) => void
  clearAuth: () => void
  updateUser: (updates: Partial<User>) => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  setAuth: (user, token) => set({ user, token, isAuthenticated: true }),
  clearAuth: () => set({ user: null, token: null, isAuthenticated: false }),
  updateUser: (updates) =>
    set((state) => ({
      user: state.user ? { ...state.user, ...updates } : null,
    })),
}))

// Hydrate the in-memory store from localStorage on startup. Called once
// from App.tsx before the router renders so a refreshed page restores
// the authenticated session immediately.
export function hydrateAuthFromStorage(): void {
  try {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')
    if (token && userStr) {
      const user = JSON.parse(userStr) as User
      useAuthStore.getState().setAuth(user, token)
    }
  } catch {
    // Corrupted localStorage entry; clear and continue unauthenticated.
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }
}
